import React, { createRef, useEffect, useState } from "react";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "@/components/Buttons.jsx";
import { FaGripLines, FaTrash, FaTruck, FaList } from "react-icons/fa6";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { updateAboutUsPage, getAboutUsPage } from "@/api/settings";
import { useDispatch, useSelector } from "react-redux";
import htmlToDraft from "html-to-draftjs";
import ReactGridLayout from "react-grid-layout";
import { addToast } from "@/store/app/action.js";

function makeid(length) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

const Customize = ({ type = "store" }) => {
  const [sections, setSections] = useState([]);
  const [lgLayout, setLgLayout] = useState([]);
  const [smLayout, setSmLayout] = useState([]);
  const { selectedLocation } = useSelector((state) => state.app);
  const [currentView, setCurrentView] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const data = (await getAboutUsPage(type))?.page || {};

      const newSections = data.sections.map((section) => {
        const ref = createRef();
        ref.current = EditorState.createWithContent(
          ContentState.createFromBlockArray(
            htmlToDraft(section.content).contentBlocks
          )
        );

        return {
          id: section.id,
          content: section.content,
          state: ref,
          editorRef: createRef(),
          mobileEditorRef: createRef(),
        };
      });
      setSections(newSections);
      setLgLayout(data.layouts.lg);
      setSmLayout(data.layouts.sm);
    };
    fetchData();
  }, [selectedLocation]);

  const save = async () => {
    setSaving(true);
    let payload = {
      layouts: {
        lg: lgLayout,
        sm: smLayout,
      },
      sections: sections.map((section) => {
        return {
          id: section.id,
          content: draftToHtml(
            convertToRaw(section.state.current.getCurrentContent())
          ),
        };
      }),
    };
    try {
      await updateAboutUsPage(payload, type);
      setSaved(true);
      dispatch(
        addToast({
          type: "success",
          message: "About us page saved successfully",
          timer: 5,
        })
      );
      setTimeout(() => {
        setSaved(false);
      }, 5000);
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.message;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}

      dispatch(
        addToast({
          type: "error",
          message: "Error creating stripe account, " + message,
          timer: 5,
        })
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grow">
      {/* Panel body */}
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4">
            About Us Page
            <div
              className={
                "transition-all duration-300 inline-block ml-2 cursor-pointer " +
                (collapsed ? "" : "scale-y-[-1]")
              }
              onClick={() => setCollapsed(!collapsed)}
            >
              <svg
                className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400"
                viewBox="0 0 12 12"
              >
                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
              </svg>
            </div>
          </h2>
        </div>
        <div
          className={`flex flex-col  gap-3 overflow-hidden transition-all duration-700 ${
            collapsed ? "h-0" : "h-auto"
          }`}
        >
          <div className="text-sm">
            Design your about us page to inform your customers about your
            business.
          </div>
          <div className={"flex gap-2"}>
            <PrimaryButton
              size={"sm"}
              onClick={save}
              success={saved}
              loading={saving}
            >
              Save
            </PrimaryButton>
          </div>
          <div className={"flex gap-2"}>
            <SecondaryButton
              size={"sm"}
              onClick={() => {
                // generate random id
                const id = "section_" + makeid(10);
                const ref = createRef();
                ref.current = EditorState.createEmpty();
                setSections([
                  ...sections,
                  {
                    id,
                    content: "",
                    state: ref,
                    editorRef: createRef(),
                    mobileEditorRef: createRef(),
                  },
                ]);
                setLgLayout([
                  ...lgLayout,
                  { i: id, x: 0, y: 0, w: 1, h: 2, minW: 1, maxW: 1 },
                ]);
                setSmLayout([
                  ...smLayout,
                  { i: id, x: 0, y: 0, w: 1, h: 2, minW: 1, maxW: 1 },
                ]);
              }}
            >
              Add Half Width Section
            </SecondaryButton>
            <SecondaryButton
              size={"sm"}
              onClick={() => {
                // generate random id
                const id = "section_" + makeid(10);
                const ref = createRef();
                ref.current = EditorState.createEmpty();
                setSections([
                  ...sections,
                  {
                    id,
                    content: "",
                    state: ref,
                    editorRef: createRef(),
                    mobileEditorRef: createRef(),
                  },
                ]);
                setLgLayout([
                  ...lgLayout,
                  { i: id, x: 0, y: 0, w: 2, h: 2, minW: 2, maxW: 2 },
                ]);
                setSmLayout([
                  ...smLayout,
                  { i: id, x: 0, y: 0, w: 1, h: 2, minW: 1, maxW: 1 },
                ]);
              }}
            >
              Add Full Width Section
            </SecondaryButton>
          </div>

          <div className="flex border-b border-slate-200 dark:border-slate-700 w-full gap-4">
            <ul className="text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar">
              <li
                className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                onClick={() => {
                  setCurrentView("desktop");
                }}
              >
                <span
                  className={
                    "cursor-pointer whitespace-nowrap flex items-center " +
                    (currentView === "desktop"
                      ? "text-indigo-500 dark:text-indigo-400"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                  }
                >
                  <FaList
                    className="w-6 h-6
                     shrink-0 fill-current  mr-2"
                  />
                  <span>Desktop</span>
                </span>
              </li>
            </ul>
            <ul className="text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar">
              <li
                className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                onClick={() => {
                  setCurrentView("mobile");
                }}
              >
                <span
                  className={
                    "cursor-pointer whitespace-nowrap flex items-center " +
                    (currentView === "mobile"
                      ? "text-indigo-500 dark:text-indigo-400"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                  }
                >
                  <FaList
                    className="w-6 h-6
                     shrink-0 fill-current  mr-2"
                  />
                  <span>Mobile</span>
                </span>
              </li>
            </ul>
          </div>

          {currentView === "desktop" && (
            <>
              <ReactGridLayout
                className="layout"
                layout={lgLayout}
                cols={2}
                draggableCancel=".non-draggable"
                width={1024}
                onLayoutChange={(layout, layouts) => {
                  setLgLayout(layout);
                }}
              >
                {sections.map((section) => (
                  <div key={section.id} className={"border flex flex-col"}>
                    <div className={"flex justify-end py-1 gap-2 items-center"}>
                      <div className="non-draggable">
                        <DangerButton
                          size="xs"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSections(
                              sections.filter((s) => s.id !== section.id)
                            );
                            setLgLayout(
                              lgLayout.filter((l) => l.i !== section.id)
                            );
                            setSmLayout(
                              smLayout.filter((l) => l.i !== section.id)
                            );
                          }}
                          naked
                        >
                          <FaTrash />
                        </DangerButton>
                      </div>

                      <div className="text-lg">
                        <FaGripLines />
                      </div>
                    </div>
                    <div
                      className={"z-[10000] non-draggable p-2"}
                      ref={section.editorRef}
                    >
                      <Editor
                        editorState={section.state.current}
                        editorClassName="form-input w-full h-full"
                        onEditorStateChange={(state) => {
                          const index = sections.findIndex(
                            (s) => s.id === section.id
                          );
                          const newSections = [...sections];
                          newSections[index].state.current = state;
                          setSections(newSections);
                          // check height
                          const newGridHeight = Math.ceil(
                            Math.max(
                              0,
                              Math.floor(
                                section.editorRef.current.clientHeight / 140
                              ) - 1
                            ) + 2
                          );
                          const newLayout = lgLayout.map((l) =>
                            l.i === section.id ? { ...l, h: newGridHeight } : l
                          );
                          setLgLayout(newLayout);
                        }}
                        toolbar={{
                          // options: ["inline", "list"],
                          inline: {
                            inDropdown: false,
                            options: [
                              "bold",
                              "italic",
                              "underline",
                              "superscript",
                              "subscript",
                            ],
                          },
                          list: { inDropdown: false },
                          textAlign: { inDropdown: false, options: [] },
                          link: {
                            inDropdown: false,
                            options: [],
                            component: null,
                          },
                          image: {
                            uploadEnabled: false,
                            options: [],
                            component: null,
                          },
                          embedded: { options: [], component: null },
                          remove: {
                            className: undefined,
                            component: undefined,
                          },

                          history: {
                            inDropdown: false,
                            options: [],
                            component: null,
                          },
                        }}
                      />
                    </div>
                  </div>
                ))}
              </ReactGridLayout>
            </>
          )}
          {currentView === "mobile" && (
            <>
              <ReactGridLayout
                className="layout"
                layout={smLayout}
                cols={1}
                draggableCancel=".non-draggable"
                width={512}
                onLayoutChange={(layout, layouts) => {
                  setSmLayout(layout);
                }}
              >
                {sections.map((section) => (
                  <div key={section.id} className={"border flex flex-col"}>
                    <div className={"flex justify-end py-1 gap-2 items-center"}>
                      <div className="non-draggable">
                        <DangerButton
                          size="xs"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSections(
                              sections.filter((s) => s.id !== section.id)
                            );
                            setLgLayout(
                              lgLayout.filter((l) => l.i !== section.id)
                            );
                            setSmLayout(
                              smLayout.filter((l) => l.i !== section.id)
                            );
                          }}
                          naked
                        >
                          <FaTrash />
                        </DangerButton>
                      </div>

                      <div className="text-lg">
                        <FaGripLines />
                      </div>
                    </div>
                    <div
                      className={"z-[10000] non-draggable p-2"}
                      ref={section.mobileEditorRef}
                    >
                      <Editor
                        editorState={section.state.current}
                        editorClassName="form-input w-full h-full"
                        onEditorStateChange={(state) => {
                          const index = sections.findIndex(
                            (s) => s.id === section.id
                          );
                          const newSections = [...sections];
                          newSections[index].state.current = state;
                          setSections(newSections);
                          // check height
                          const newGridHeight = Math.ceil(
                            Math.max(
                              0,
                              Math.floor(
                                section.mobileEditorRef.current.clientHeight /
                                  140
                              ) - 1
                            ) + 2
                          );
                          const newLayout = lgLayout.map((l) =>
                            l.i === section.id ? { ...l, h: newGridHeight } : l
                          );
                          setSmLayout(newLayout);
                        }}
                        toolbar={{
                          // options: ["inline", "list"],
                          inline: {
                            inDropdown: false,
                            options: [
                              "bold",
                              "italic",
                              "underline",
                              "superscript",
                              "subscript",
                            ],
                          },
                          list: { inDropdown: false },
                          textAlign: { inDropdown: false, options: [] },
                          link: {
                            inDropdown: false,
                            options: [],
                            component: null,
                          },
                          image: {
                            uploadEnabled: false,
                            options: [],
                            component: null,
                          },
                          embedded: { options: [], component: null },
                          remove: {
                            className: undefined,
                            component: undefined,
                          },

                          history: {
                            inDropdown: false,
                            options: [],
                            component: null,
                          },
                        }}
                      />
                    </div>
                  </div>
                ))}
              </ReactGridLayout>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Customize;
